// Admin sidebar navigation sections — mirrors CIEDA back-office structure exactly.
// Each top-level item links to a hub page; sub-pages are accessible from within.

import type { LucideIcon } from "lucide-react";
import {
  LayoutDashboard,
  ShoppingCart,
  Cog,
  Heart,
  DollarSign,
  BarChart3,
  Settings,
  Truck,
  Boxes,
} from "lucide-react";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface NavItem {
  readonly labelKey: string;
  readonly path: string;
  readonly permission?: string;
  /**
   * When set, this nav item is only visible if the corresponding module flag
   * is enabled. Uses the `module_key` column from the `feature_flags` table.
   */
  readonly moduleKey?: string;
}

export interface NavSection {
  readonly key: string;
  readonly labelKey: string;
  readonly icon: LucideIcon;
  readonly items: readonly NavItem[];
}

// ---------------------------------------------------------------------------
// Sections — exact mirror of CIEDA back-office AdminSidebar NAV_GROUPS
// ---------------------------------------------------------------------------

export const NAV_SECTIONS: readonly NavSection[] = [
  // 1. Principal
  {
    key: "principal",
    labelKey: "nav.sections.principal",
    icon: LayoutDashboard,
    items: [
      { labelKey: "nav.dashboard", path: "/admin", permission: "dashboard" },
    ],
  },

  // 2. Commerce
  {
    key: "commerce",
    labelKey: "nav.sections.commerce",
    icon: ShoppingCart,
    items: [
      { labelKey: "nav.orders", path: "/admin/orders", permission: "orders" },
      { labelKey: "nav.products", path: "/admin/products", permission: "products" },
      { labelKey: "nav.clients_360", path: "/admin/clients", permission: "customers" },
      { labelKey: "nav.payment_manager", path: "/admin/payments", permission: "payments" },
    ],
  },

  // 3. Opérations
  {
    key: "operations",
    labelKey: "nav.sections.operations",
    icon: Cog,
    items: [
      { labelKey: "nav.logistics", path: "/admin/logistique", permission: "logistics", moduleKey: "logistics" },
      { labelKey: "nav.operations_hub", path: "/admin/operations", permission: "operations" },
      { labelKey: "nav.reports", path: "/admin/reports", permission: "reports" },
    ],
  },

  // 4. Croissance
  {
    key: "croissance",
    labelKey: "nav.sections.croissance",
    icon: Heart,
    items: [
      { labelKey: "nav.crm", path: "/admin/segments", permission: "customers", moduleKey: "crm" },
      { labelKey: "nav.marketing_hub", path: "/admin/promotions", permission: "cms" },
      { labelKey: "nav.b2b_hub", path: "/admin/b2b/customers", permission: "customers" },
      { labelKey: "nav.ai_dashboard", path: "/admin/ai", permission: "analytics" },
      { labelKey: "nav.lancement", path: "/admin/lancement", permission: "operations" },
    ],
  },

  // 5. Gestion
  {
    key: "gestion",
    labelKey: "nav.sections.gestion",
    icon: DollarSign,
    items: [
      { labelKey: "nav.finance_hub", path: "/admin/revenue", permission: "reports", moduleKey: "finance-dashboard" },
    ],
  },

  // 6. Analytique
  {
    key: "analytique",
    labelKey: "nav.sections.analytique",
    icon: BarChart3,
    items: [
      { labelKey: "nav.analytics_overview", path: "/admin/analytics", permission: "analytics" },
      { labelKey: "nav.analytics_trending", path: "/admin/analytics/trending", permission: "analytics" },
      { labelKey: "nav.analytics_search", path: "/admin/analytics/search", permission: "analytics" },
      { labelKey: "nav.analytics_funnel", path: "/admin/analytics/funnel", permission: "analytics" },
      { labelKey: "nav.analytics_intent", path: "/admin/analytics/intent", permission: "analytics" },
    ],
  },

  // 7. Yoboulmako
  {
    key: "yoboulmako",
    labelKey: "nav.sections.yoboulmako",
    icon: Truck,
    items: [
      { labelKey: "nav.yoboulmako_dashboard", path: "/admin/yoboulmako", permission: "operations" },
      { labelKey: "nav.yoboulmako_livraisons", path: "/admin/yoboulmako/livraisons", permission: "logistics" },
      { labelKey: "nav.yoboulmako_dispatches", path: "/admin/yoboulmako/dispatches", permission: "logistics" },
      { labelKey: "nav.yoboulmako_clients", path: "/admin/yoboulmako/clients", permission: "customers" },
      { labelKey: "nav.yoboulmako_facturation", path: "/admin/yoboulmako/facturation", permission: "reports" },
      { labelKey: "nav.yoboulmako_parametres", path: "/admin/yoboulmako/parametres", permission: "settings" },
    ],
  },

  // 8. DJAYMA engine consoles
  {
    key: "djayma",
    labelKey: "nav.sections.djayma",
    icon: Boxes,
    items: [
      { labelKey: "nav.djayma_control_tower", path: "/admin/djayma", permission: "operations" },
      { labelKey: "nav.djayma_finance", path: "/admin/djayma/finance", permission: "reports" },
      { labelKey: "nav.djayma_call_center", path: "/admin/djayma/call-center", permission: "customers" },
      { labelKey: "nav.djayma_stock", path: "/admin/djayma/stock", permission: "products" },
      { labelKey: "nav.djayma_orders", path: "/admin/djayma/commandes", permission: "orders" },
      { labelKey: "nav.djayma_logistique", path: "/admin/djayma/logistique", permission: "logistics" },
      { labelKey: "nav.djayma_pricing", path: "/admin/djayma/prix", permission: "products" },
      { labelKey: "nav.djayma_promotions", path: "/admin/djayma/promotions", permission: "cms" },
      { labelKey: "nav.djayma_partners", path: "/admin/djayma/partenaires", permission: "logistics" },
      { labelKey: "nav.djayma_channels", path: "/admin/djayma/canaux", permission: "customers" },
      { labelKey: "nav.djayma_access", path: "/admin/djayma/acces", permission: "settings" },
    ],
  },
  // 9. Administration
  {
    key: "administration",
    labelKey: "nav.sections.administration",
    icon: Settings,
    items: [
      { labelKey: "nav.content", path: "/admin/contenu", permission: "cms" },
      { labelKey: "nav.settings", path: "/admin/modules", permission: "settings" },
      { labelKey: "nav.privacy", path: "/admin/vie-privee", permission: "settings" },
      { labelKey: "nav.enterprise", path: "/admin/entreprise", permission: "settings" },
      { labelKey: "nav.audit_log", path: "/admin/audit-log", permission: "settings" },
    ],
  },
] as const;
